import { ActionIcon, Button, Center, Group, Loader, Stack, Table, Text, Tooltip } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDownload, IconEdit, IconMessageCircle, IconQrcode } from '@tabler/icons-react'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { WargaSuratStatusBadge } from '../../../../components/warga/WargaSuratStatusBadge'
import { useGenerateLengkapiLink, useWargaSurat } from '../../../../hooks/useApi'

export const Route = createFileRoute('/desa/$slug/warga/surat')({
  component: WargaSuratPage,
})

function WargaSuratPage() {
  const { data: surat, isLoading } = useWargaSurat()
  const params = useParams({ strict: false }) as { slug?: string }
  const slug = params.slug ?? ''
  const navigate = useNavigate()
  // BUG-G4-7/G4-6: link FormWizard di-mint on-demand agar warga bisa perbaiki data (bukan cuma reset status)
  const lengkapiLink = useGenerateLengkapiLink()

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    )
  }

  return (
    <Stack gap="md">
      {!surat?.length ? (
        <Stack align="center" py="xl" gap="sm">
          <Text c="dimmed" ta="center">
            Belum ada surat. Ajukan surat melalui chat AI desa.
          </Text>
          <Button
            variant="light"
            color="teal"
            size="sm"
            leftSection={<IconMessageCircle size={16} />}
            onClick={() => navigate({ to: `/desa/${slug}/warga/chat` as any })}
          >
            Chat dengan AI Desa
          </Button>
        </Stack>
      ) : (
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Jenis</Table.Th>
                <Table.Th>Tanggal</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Nomor</Table.Th>
                <Table.Th>Aksi</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {surat.map((s: any) => (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    <Text size="sm">{s.jenis}</Text>
                    {s.keperluan && (
                      <Text size="xs" c="dimmed">
                        {s.keperluan}
                      </Text>
                    )}
                    {s.status === 'perlu_dilengkapi' && s.catatan_kelengkapan && (
                      <Text size="xs" c="orange">
                        Perlu dilengkapi: {s.catatan_kelengkapan}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{new Date(s.created_at).toLocaleDateString('id-ID')}</Text>
                  </Table.Td>
                  <Table.Td>
                    <WargaSuratStatusBadge status={s.status} isRevoked={s.is_revoked} />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{s.nomor_surat ?? '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {s.pdf_url && !s.is_revoked && (
                        <Tooltip label="Download PDF">
                          <ActionIcon variant="light" color="blue" component="a" href={s.pdf_url} target="_blank">
                            <IconDownload size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {s.kode_verifikasi && !s.is_revoked && (
                        <Tooltip label="Verifikasi QR">
                          <ActionIcon
                            variant="light"
                            color="green"
                            component="a"
                            href={`/verify/${s.kode_verifikasi}`}
                            target="_blank"
                          >
                            <IconQrcode size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {s.status === 'perlu_dilengkapi' && (
                        <Button
                          size="xs"
                          variant="light"
                          color="orange"
                          leftSection={<IconEdit size={14} />}
                          loading={lengkapiLink.isPending}
                          onClick={() =>
                            lengkapiLink.mutate(s.id, {
                              onSuccess: (res) => {
                                window.location.href = res.url
                              },
                              onError: (e: Error) =>
                                notifications.show({ title: 'Gagal', message: e.message, color: 'red' }),
                            })
                          }
                        >
                          Lengkapi Sekarang
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  )
}
